import { CheckIcon } from "@heroicons/react/16/solid";
import { BuildingOfficeIcon, ServerIcon } from "@heroicons/react/20/solid";
import ChevronUpDownIcon from "@heroicons/react/24/outline/ChevronUpDownIcon";
import { get } from "lodash-es";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";

import {
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  SvgIcon,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { SearchIcon } from "../../components/icons";
import TextField from "../../components/styled/TextField";
import { usePopover } from "../../hooks/use-popover";
import { getBasePath } from "../../hooks/use-reset-redirect";
import { setClusterAndOrg, setOrgId } from "../../slices/current";
import { setOrgClusterOpen } from "../../slices/organizations";
import { useDispatch, useSelector } from "../../store";
import { useListClustersQuery } from "../../store/pomeriumApi";
import { HostedChip } from "@/components/HostedChip";

const OrgClusterDialog = (): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const popover = usePopover<HTMLButtonElement>();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [localOrgId, setLocalOrgId] = useState(organizationId);
  const { orgClusterUiOpen, byId } = useSelector(
    (state) => state.organizations,
  );
  const [selected, setSelected] = useState("");
  const [searchText, setSearchText] = useState("");
  const numOrg = Object.values(byId)?.length || 0;
  const { data: clusters } = useListClustersQuery(localOrgId, {
    skip: !localOrgId,
  });

  // always set localOrgId (the initial selected dropdown menu item) as the current org
  useEffect(() => setLocalOrgId(organizationId), [organizationId]);

  const onClose = () => dispatch(setOrgClusterOpen(false));

  const gotoManageOrgs = () => {
    onClose();
    dispatch(setOrgId(localOrgId));
    navigate("/app/organizations?oid=" + localOrgId);
  };

  const gotoManageClusters = () => {
    onClose();
    dispatch(setOrgId(localOrgId));
    navigate("/app/clusters?oid=" + localOrgId);
  };

  const handleOrganizationChange = (id: string): void => {
    popover.handleClose();
    setLocalOrgId(id);
  };

  const handleClusterChange = (id: string) => () => {
    onClose();
    const cluster = clusters?.find((c) => c.id === id);

    dispatch(
      setClusterAndOrg({
        clusterId: id,
        organizationId: localOrgId,
        flavor: cluster?.flavor,
      }),
    );
    navigate(
      {
        pathname: getBasePath(pathname),
        search: createSearchParams({
          oid: localOrgId,
          cid: id,
        }).toString(),
      },
      { replace: true },
    );
  };

  const handleSelect = useCallback(
    (id: string) => () => {
      setSelected(id);
    },
    [],
  );

  const saveSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (!orgClusterUiOpen) {
      setSelected("");
      setLocalOrgId(organizationId);
    }
  }, [orgClusterUiOpen]);

  return (
    <Dialog
      open={orgClusterUiOpen}
      onClose={onClose}
      scroll="paper"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <Stack spacing={2}>
          <Typography variant="h4">
            {numOrg > 1 ? "Select an organization" : "Organization"}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            onClick={popover.handleOpen}
            ref={popover.anchorRef}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIcon fontSize="small">
                <BuildingOfficeIcon />
              </SvgIcon>
              <Typography
                component="span"
                color="inherit"
                sx={{
                  fontSize: 14,
                  lineHeight: "24px",
                }}
              >
                {get(byId, localOrgId + ".name", "") as string}
              </Typography>
              <SvgIcon color="action" fontSize="small" sx={{ ml: 3 }}>
                <ChevronUpDownIcon />
              </SvgIcon>
            </Stack>
          </Button>
          <Popover
            anchorEl={popover.anchorRef.current}
            anchorOrigin={{
              horizontal: "left",
              vertical: "bottom",
            }}
            disableScrollLock
            onClose={popover.handleClose}
            open={popover.open}
            slotProps={{
              paper: {
                sx: {
                  p: 1,
                  minWidth: 210,
                },
              },
            }}
          >
            <MenuList dense sx={{ p: 0 }}>
              {Object.values(byId)?.map((o) => {
                const isSelected = o.id === organizationId;

                return (
                  <MenuItem
                    key={"org" + o.id + o.name}
                    onClick={() => handleOrganizationChange?.(o.id)}
                    selected={isSelected}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText>
                      {o.organizationType === "professional"
                        ? o.name
                        : "Personal Account"}
                    </ListItemText>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Popover>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack sx={{ mt: 1, minHeight: (clusters?.length || 3) * 100 }}>
          <TextField
            placeholder="Search Clusters"
            value={searchText}
            onChange={saveSearchText}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Table>
            <TableBody>
              <TableRow>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Id</TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              {clusters
                ?.filter((c) => {
                  if (!searchText.trim()) {
                    return c;
                  } else {
                    return (
                      c?.name
                        ?.toLocaleLowerCase()
                        .indexOf(searchText.trim().toLocaleLowerCase()) > -1
                    );
                  }
                })
                .map((c) => (
                  <TableRow
                    key={"cluster_" + c?.id}
                    onDoubleClick={handleClusterChange(c?.id)}
                    onClick={handleSelect(c?.id)}
                    selected={c?.id === selected}
                    hover
                  >
                    <TableCell>
                      {c?.id === clusterId && (
                        <SvgIcon fontSize="small" color="success">
                          <CheckIcon />
                        </SvgIcon>
                      )}
                    </TableCell>
                    <TableCell>
                      <SvgIcon fontSize="small">
                        <ServerIcon />
                      </SvgIcon>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Link
                          color="primary"
                          onClick={handleClusterChange(c?.id)}
                          sx={{ cursor: "pointer" }}
                        >
                          {c?.name}
                        </Link>
                        {c?.flavor === "hosted" ? <HostedChip /> : null}
                      </div>
                    </TableCell>
                    <TableCell>{c?.id}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={gotoManageOrgs}>
          Manage Organization
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={gotoManageClusters}
        >
          Manage Clusters
        </Button>
        <Button variant="contained" color="warning" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrgClusterDialog;
